import { Link, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Ion } from "@/components/Ion";
import { useAuth } from "@/context/AuthContext";
import { accentFor } from "@/lib/roles";
import { EmailConnectBanner } from "@/components/EmailConnectBanner";

// Shared page frame for every screen inside the portal.
export const PortalPage = ({ eyebrow, title, back = "/dashboard", backLabel = "Back", actions, inlineActions = false, children, testId, width = "max-w-3xl" }) => {
  const { user } = useAuth();
  const accent = accentFor(user?.role);
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-[#0A0A0A] font-body text-white" style={{ "--accent": accent }} data-testid={testId}>
      <main className="relative w-full px-6 pb-32">
        <header className="-mx-6 mb-2 flex items-center gap-4 bg-[#0A0A0A] px-6 pb-4 pt-6">
          {back && (
            <button data-testid="page-back" aria-label={backLabel} onClick={() => (typeof back === "string" ? navigate(back) : navigate(-1))} className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] border border-[#333] bg-[#1A1A1A] text-white">
              <Ion name="chevron-back" size={22} color="#FFFFFF" />
            </button>
          )}
          <div className={`min-w-0 ${inlineActions ? "shrink-0" : "flex-1"}`}>
            {eyebrow && <p className="truncate text-[11px] font-bold tracking-[3px]" style={{ color: accent }}>{eyebrow}</p>}
            <h1 className="mt-0.5 truncate text-[22px] font-extrabold text-white" data-testid="page-title">{title}</h1>
          </div>
          <div className={`flex items-center gap-2 ${inlineActions ? "flex-wrap" : "shrink-0"}`}>{actions}</div>
        </header>
        <div className="space-y-4"><EmailConnectBanner />{children}</div>
      </main>
    </div>
  );
};

export const Card = ({ children, className = "", style, ...rest }) => (
  <div {...rest} className={`rounded-[14px] border border-[#333] bg-[#1A1A1A] p-4 ${className}`} style={style}>{children}</div>
);

export const Btn = ({ children, variant = "primary", className = "", busy, ...props }) => {
  const base = "flex min-h-[48px] w-full items-center justify-center gap-2 rounded-lg px-4 py-3 text-[13px] font-extrabold tracking-[1.5px] transition-opacity duration-150 disabled:opacity-40";
  const styles = variant === "primary"
    ? { background: "var(--accent)", color: "#000" }
    : { border: "1px solid var(--accent)", color: "var(--accent)" };
  return (
    <button {...props} disabled={busy || props.disabled} className={`${base} ${className}`} style={styles}>
      {busy ? <Loader2 size={18} className="animate-spin" /> : children}
    </button>
  );
};

export const Spinner = () => (
  <div className="flex min-h-[40vh] items-center justify-center" data-testid="page-loading"><Loader2 className="animate-spin text-[var(--accent)]" /></div>
);

export const ErrorNote = ({ children }) => children ? <p className="rounded-lg border border-[#FF3333]/50 bg-[#FF3333]/10 p-3 text-sm text-[#ff8080]" data-testid="page-error">{children}</p> : null;

export const TextLink = ({ to, children, ...rest }) => <Link to={to} {...rest} className="text-xs font-bold text-[var(--accent)] hover:underline">{children}</Link>;
